import { AgGridReact } from "ag-grid-react";
import { Pagination, Spin } from "antd";
import { FC, useCallback, useEffect, useState } from "react";
import { BiPlus } from "react-icons/bi";
import { isMobile, isTablet } from "react-device-detect";

import Button from "../Button";
import useTable, {
  HEADER_HEIGHT,
  Props,
  ReceivedProps,
  ROW_HEIGHT,
} from "./hook";
import {
  SelectWrapper,
  Styles,
  StylesPagination,
  TableWrapper,
} from "./styled";
import CustomVerticalScrollBar from "../CustomVerticalScrollBar";
import CustomHorizontalScrollBar from "../CustomHorizontalScrollBar";
import ButtonCommon from "../ButtonCommon";
import ToolbarSeparator, {
  TOOLBAR_SEPARATOR_HEIGHT,
} from "../ToolbarSeparator";
import FilterWrapper from "./components/FilterWrapper";

const TableLayout: FC<Props> = ({
  // 🔹 Core
  gridRef,
  rowData,
  columnDefs,
  defaultColDef = {},
  onGridReady,
  onGridSizeChanged,
  loading,
  totalCellHeader,
  // 🔹 Layout & Style
  containerStyle,
  gridStyle,
  rowHeight,
  headerHeight,
  scrollbarWidth = 8,
  alwaysShowVerticalScroll = true,
  noFitColumns = false,
  styleVerticalScrollBar,
  isFullWidthRow,
  domLayout,

  // 🔹 Pagination
  page,
  total,
  pageSize = 50,
  showPagination = true,
  isCustomPagination = false,
  setPage,
  setPageSize,
  isShowPaginationHeader = false,

  // 🔹 Header
  headerChildren,
  preHeaderChildren,
  floatingFiltersHeight = 48,

  // 🔹 Selection
  rowSelection,
  suppressRowClickSelection = false,
  suppressRowHoverHighlight = false,
  onSelectionChanged = () => {},

  // 🔹 Row events
  onRowClicked = () => {},
  getRowClass,
  getRowHeight = undefined,
  getRowId,

  // 🔹 Column events
  onColumnResized = () => {},
  onColumnMoved = undefined,
  onVirtualColumnsChanged,

  // 🔹 Drag & Drop
  rowDragManaged = false,
  animateRows = false,
  onRowDragEnd = undefined,
  onDragStopped = () => {},

  // 🔹 Filter
  onFilterUpdate,
  clearFilter,
  onSortChanged,
  suppressMenuHide = false,
  agLocaleText,

  // 🔹 Extra features
  isShowBtnCreate = true,
  onCreate,
  singleClickEdit,
  isShowButtonEnd = false,
  suppressMovableColumns,
  gridDivRef,
  tableContainerRef,
  btnGroupRef,
  scrollContainerBarVerticalRef,
  handleShowVerticalScrollBar,
  widthScrollVertical,
  setWidthScrollVertical,
  heightScrollHorizontal,
  setHeightScrollHorizontal,
  scrollContainerBarHorizontalRef,
  height,
  size,
  displaySize,
  setIsOpenQuickView,
  context,
  handleFilterUpdate,
  handleChangePageSize,
  handleChangePage,
  hiddenPaginationInURL,
  btnCreateHeight,
  colorHeader,
  bgHeader,
  disabledPagination = false,
  elementBetweenHeaderAndTable,
}) => {
  const renderPagination = useCallback(() => {
    if (total / pageSize <= 1) return null;
    return (
      <Pagination
        pageSize={pageSize}
        current={page}
        total={total}
        onChange={(e) => {
          setPage?.(e);
          setIsOpenQuickView?.(false);
          !hiddenPaginationInURL && handleChangePage?.(e);
        }}
        disabled={disabledPagination}
      />
    );
  }, [
    total,
    pageSize,
    page,
    setPage,
    setIsOpenQuickView,
    handleChangePage,
    hiddenPaginationInURL,
  ]);

  const { filterInfo, handleSetFilterInfo } = context;
  const [isHasScrollVertical, setIsHasScrollVertical] = useState(false);
  const isTouchDevice = isMobile || isTablet;
  const shouldUseCustomScrollbars = !isTouchDevice;

  useEffect(() => {
    if (shouldUseCustomScrollbars) return;
    setWidthScrollVertical(0);
    setHeightScrollHorizontal(0);
  }, [
    shouldUseCustomScrollbars,
    setHeightScrollHorizontal,
    setWidthScrollVertical,
  ]);

  const isDisplayTopButton =
    clearFilter ||
    isShowBtnCreate ||
    headerChildren ||
    preHeaderChildren ||
    isShowPaginationHeader;
  const resolvedFloatingFiltersHeight =
    floatingFiltersHeight > 0 ? floatingFiltersHeight + 16 : 0;

  const eGridDiv = gridRef?.current?.api?.context?.contextParams
    ?.providedBeanInstances?.eGridDiv as HTMLDivElement;
  const agBodyViewport = eGridDiv?.querySelector(
    ".ag-body-viewport",
  ) as HTMLDivElement;
  const agCenterColsContainer = eGridDiv?.querySelector(
    ".ag-center-cols-container",
  ) as HTMLDivElement;

  const agBodyHorizontalScrollViewport = eGridDiv?.querySelector(
    ".ag-body-horizontal-scroll-viewport",
  ) as HTMLDivElement;
  const isBodyScrollable =
    agBodyViewport?.scrollHeight > agBodyViewport?.offsetHeight;

  useEffect(() => {
    const isHasScrollVerticalNew = Boolean(isBodyScrollable);
    if (isHasScrollVertical !== isHasScrollVerticalNew) {
      setIsHasScrollVertical(isHasScrollVerticalNew);
    }
  }, [isBodyScrollable, isHasScrollVertical]);

  return (
    <Spin spinning={!!loading}>
      <Styles ref={gridDivRef} totalCellHeader={totalCellHeader}>
        <div
          className={
            "table-wrapper" + (!isDisplayTopButton ? " table-no-header" : "")
          }
        >
          {isDisplayTopButton ? (
            <div
              ref={btnGroupRef}
              className="btn-group"
              style={{
                justifyContent: isShowButtonEnd ? "flex-end" : "flex-start",
              }}
            >
              {preHeaderChildren}
              {clearFilter && (
                <Button
                  label="検索クリア"
                  className="btn-clear"
                  onClick={clearFilter}
                />
              )}
              {isShowBtnCreate && !displaySize ? (
                <Button
                  label="新規追加"
                  icon={<BiPlus size={18} />}
                  className="btn-create"
                  onClick={onCreate}
                />
              ) : isShowBtnCreate && displaySize ? (
                <ButtonCommon
                  name="新規追加"
                  icon={<BiPlus size={18} />}
                  className="btn-create"
                  onClick={onCreate}
                  style={{
                    height: btnCreateHeight || height,
                    fontSize: size,
                    width: "fit-content",
                    minWidth: "unset",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "5px",
                  }}
                />
              ) : (
                ""
              )}
              {headerChildren}
              {showPagination && isShowPaginationHeader && (
                <ToolbarSeparator
                  height={height ? height + 5 : TOOLBAR_SEPARATOR_HEIGHT}
                />
              )}
              {showPagination && isShowPaginationHeader && (
                <StylesPagination
                  text_scale={displaySize}
                  $disabled={disabledPagination}
                  style={{ marginLeft: 0 }}
                >
                  <div className="footer" style={{ marginTop: "0px" }}>
                    <div className="pagination">
                      <div className="select">
                        <div
                          style={{
                            fontSize: `${size}px`,
                            whiteSpace: "nowrap",
                          }}
                        >
                          表示 &nbsp;:
                        </div>
                        <SelectWrapper
                          disabled={disabledPagination}
                          $fontSize={size}
                          $height={height}
                          defaultValue={pageSize}
                          onChange={(e) => {
                            setPage?.(1);
                            setPageSize?.(Number(e));
                            setIsOpenQuickView?.(false);
                            !hiddenPaginationInURL &&
                              handleChangePageSize?.(Number(e));
                          }}
                          style={{
                            fontSize: `${size}px`,
                          }}
                          options={
                            isCustomPagination
                              ? [
                                  {
                                    value: "0",
                                    label: "All",
                                  },
                                  {
                                    value: "100",
                                    label: "100",
                                  },
                                ]
                              : [
                                  {
                                    value: "10",
                                    label: "10",
                                  },
                                  {
                                    value: "20",
                                    label: "20",
                                  },
                                  {
                                    value: "30",
                                    label: "30",
                                  },
                                  {
                                    value: "50",
                                    label: "50",
                                  },
                                ]
                          }
                        />
                      </div>
                      {renderPagination()}
                    </div>
                  </div>
                </StylesPagination>
              )}
            </div>
          ) : null}

          {elementBetweenHeaderAndTable}

          <div
            style={{
              position: "relative",
            }}
          >
            {shouldUseCustomScrollbars && (
              <div
                ref={scrollContainerBarHorizontalRef}
                className="wrapper-scroll-bar-horizontal"
              >
                <CustomHorizontalScrollBar
                  element={agBodyHorizontalScrollViewport}
                  widthOffset={widthScrollVertical}
                  extendHeight={1}
                  onGetHeightScrollVertical={(h) => {
                    setHeightScrollHorizontal(h);
                  }}
                />
              </div>
            )}
            <div
              style={{
                display: "flex",
                position: "relative",
                alignItems: "end",
              }}
            >
              {shouldUseCustomScrollbars && (
                <div
                  className="wrapper-scroll-bar-vertical"
                  ref={scrollContainerBarVerticalRef}
                >
                  <CustomVerticalScrollBar
                    element={agBodyViewport}
                    scrollContentElement={agCenterColsContainer}
                    onGetWidthScrollVertical={(w) => {
                      setWidthScrollVertical((prev) => (prev !== w ? w : prev));
                    }}
                  />
                </div>
              )}
              <TableWrapper
                widthScrollVertical={widthScrollVertical}
                heightScrollHorizontal={heightScrollHorizontal}
                $bg_header={bgHeader}
                $color_header={colorHeader}
              >
                <div
                  ref={tableContainerRef}
                  style={containerStyle}
                  className={isHasScrollVertical ? "has-scroll-vertical" : ""}
                >
                  <div
                    style={{ position: "relative", ...gridStyle }}
                    className={`ag-theme-alpine ${
                      rowDragManaged ? "" : "hidden-ag-icon"
                    }`}
                  >
                    <AgGridReact
                      localeText={{
                        noRowsToShow: "データがありません。",
                        ...(agLocaleText || {}),
                      }}
                      headerHeight={headerHeight ?? HEADER_HEIGHT}
                      rowHeight={rowHeight ?? ROW_HEIGHT}
                      ref={gridRef}
                      rowData={rowData}
                      columnDefs={columnDefs}
                      scrollbarWidth={0}
                      rowDragManaged={rowDragManaged}
                      animateRows={animateRows}
                      onFilterChanged={handleFilterUpdate}
                      onSortChanged={onSortChanged}
                      suppressMenuHide={suppressMenuHide}
                      defaultColDef={defaultColDef}
                      getRowNodeId={(e: any) => e.id}
                      onGridReady={onGridReady}
                      isFullWidthRow={isFullWidthRow}
                      suppressMovableColumns={suppressMovableColumns}
                      onRowDragEnd={onRowDragEnd}
                      onRowClicked={onRowClicked}
                      onVirtualColumnsChanged={onVirtualColumnsChanged}
                      rowSelection={rowSelection}
                      onColumnMoved={onColumnMoved}
                      getRowHeight={getRowHeight}
                      suppressRowHoverHighlight={suppressRowHoverHighlight}
                      suppressRowClickSelection={suppressRowClickSelection}
                      rowMultiSelectWithClick={rowSelection === "multiple"}
                      floatingFiltersHeight={resolvedFloatingFiltersHeight}
                      alwaysShowVerticalScroll={alwaysShowVerticalScroll}
                      alwaysShowHorizontalScroll={noFitColumns}
                      onSelectionChanged={onSelectionChanged}
                      onDragStopped={() => {
                        handleShowVerticalScrollBar();
                        onDragStopped();
                      }}
                      onColumnResized={onColumnResized}
                      getRowId={getRowId}
                      singleClickEdit={singleClickEdit}
                      onGridSizeChanged={onGridSizeChanged}
                      loadingOverlayComponent={() => (
                        <Spin
                          style={{
                            color: "#174a83",
                          }}
                        />
                      )}
                      getRowClass={getRowClass}
                      domLayout={domLayout}
                      context={context}
                    />
                  </div>
                </div>
              </TableWrapper>
            </div>
          </div>
        </div>

        {showPagination && !isShowPaginationHeader && (
          <StylesPagination
            text_scale={displaySize}
            $disabled={disabledPagination}
          >
            <div className="footer">
              <div className="pagination">
                <div className="select">
                  <div style={{ whiteSpace: "nowrap" }}>表示: </div>
                  <SelectWrapper
                    defaultValue={pageSize}
                    onChange={(e) => {
                      setPage(1);
                      setPageSize(Number(e));
                      setIsOpenQuickView?.(false);
                    }}
                    disabled={disabledPagination}
                    options={
                      isCustomPagination
                        ? [
                            {
                              value: "0",
                              label: "All",
                            },
                            {
                              value: "100",
                              label: "100",
                            },
                          ]
                        : [
                            {
                              value: "10",
                              label: "10",
                            },
                            {
                              value: "20",
                              label: "20",
                            },
                            {
                              value: "30",
                              label: "30",
                            },
                            {
                              value: "50",
                              label: "50",
                            },
                          ]
                    }
                  />
                </div>

                {renderPagination()}
              </div>
            </div>
          </StylesPagination>
        )}
      </Styles>
      {context?.filterInfo?.open && (
        <FilterWrapper
          context={context}
          filterInfo={filterInfo}
          handleSetFilterInfo={handleSetFilterInfo}
          displaySize={displaySize}
        />
      )}
    </Spin>
  );
};

const Table: FC<ReceivedProps> = (props) => (
  <TableLayout {...useTable(props)} />
);

export default Table;
